/* Thalvore cart drawer (handbook §6). Vanilla, deferred, idempotent.
   - Intercepts every form[action*="/cart/add"] submit site-wide in the capture phase on window, so
     Impulse's AjaxProduct (theme.js, always preventDefault + AJAX, redirects in page mode) never runs.
   - Adds/changes go through /cart/add.js and /cart/change.js with the Section Rendering API
     (sections=tl-cart-drawer,<header id>); the returned drawer parts replace the [data-tl-part] nodes.
   - Contracts: document 'tl:cart:updated' (detail = cart JSON), 'tl:cart:open' / 'tl:cart:close',
     html.tl-cart-open, window.TLCart = { open(opener?), close(), refresh() }. */
(() => {
  if (window.TLCart) return;
  const d = document;
  const H = d.documentElement;
  const R = (window.theme && window.theme.routes) || {};
  const q = (s, c) => (c || d).querySelector(s);
  const qa = (s, c) => [...(c || d).querySelectorAll(s)];
  const root = q('[data-tl-cart]');
  const panel = root && q('[role=dialog]', root);
  const fire = (t, n, detail) => t.dispatchEvent(new CustomEvent(n, { bubbles: true, detail }));
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  const headerId = () => { const h = q('[data-section-type=header]'); return h && h.dataset.sectionId; };
  const ids = () => ['tl-cart-drawer', headerId()].filter(Boolean).join(',');
  const readCart = c => { try { return JSON.parse(q('[data-tl-part=json]', c).textContent); } catch (e) { return null; } };
  const isOpen = () => H.classList.contains('tl-cart-open');
  const closeBtn = () => q('.tl-cart__close', root);
  let cart = root && readCart(root);
  let opener;
  let seq = 0; // bumped by every add/change, so an older GET refresh never paints over a newer cart
  let hid = []; // page parts made inert while the drawer is open

  // No drawer (cart page): the header link just goes to /cart, so drop Impulse's aria-controls="CartDrawer"
  // (that element doesn't exist in page mode).
  const links = () => qa('.js-drawer-open-cart').forEach(a => {
    if (!root) { a.removeAttribute('aria-controls'); return; }
    a.setAttribute('aria-controls', 'TlCartDrawer');
    a.setAttribute('aria-haspopup', 'dialog');
    a.setAttribute('aria-expanded', isOpen());
  });

  // Modal: everything else on the page is inert while the drawer is open (the Tab trap below is the fallback).
  const inert = on => {
    if (on) hid = [...d.body.children].filter(x => x != root && !x.inert);
    hid.forEach(x => { x.inert = on; });
    if (!on) hid = [];
  };

  const say = m => {
    const l = q('[data-tl-live]', root);
    if (!l || !m) return;
    l.textContent = '';
    setTimeout(() => { l.textContent = m; }, 80);
  };

  // Swap the drawer parts from Section Rendering API HTML, keeping the dialog shell (focus, transition).
  // soft (a GET re-sync): leave the drawer alone when the server cart is the one already shown.
  function render(sections, announce, soft) {
    const html = sections && sections['tl-cart-drawer'];
    if (html && root) {
      const doc = parse(html);
      const nj = q('[data-tl-part=json]', doc);
      if (soft && nj && nj.textContent == q('[data-tl-part=json]', root).textContent) return;
      const a = d.activeElement;
      const inside = root.contains(a);
      const fid = inside && a.dataset && a.dataset.tlF;
      const body = q('.tl-cart__body', root);
      const top = body ? body.scrollTop : 0;
      const fill = q('.tl-cart__fill', root);
      const was = fill && fill.style.width;
      qa('[data-tl-part]', doc).forEach(n => {
        const o = q('[data-tl-part="' + n.dataset.tlPart + '"]', root);
        if (o) o.replaceWith(n);
      });
      const nb = q('.tl-cart__body', root);
      if (nb) nb.scrollTop = top;
      const nf = q('.tl-cart__fill', root);
      if (nf && was) { const w = nf.style.width; nf.style.width = was; void nf.offsetWidth; nf.style.width = w; }
      cart = readCart(root) || cart;
      ups();
      if (inside && !a.isConnected) ((fid && q('[data-tl-f="' + fid + '"]', root)) || closeBtn()).focus();
      if (announce) say(q('[data-tl-part=count]', root).dataset.tlSay);
    }
    // Header cart dot: take it from the re-rendered header when we have it, else toggle Impulse's class.
    const hh = sections && sections[headerId()];
    const dot = hh && q('.cart-link__bubble', parse(hh));
    qa('.cart-link__bubble').forEach(b => {
      if (dot) b.replaceWith(dot.cloneNode(true));
      else b.classList.toggle('cart-link__bubble--visible', !!cart && cart.item_count > 0);
    });
    if (cart) fire(d, 'tl:cart:updated', cart);
  }

  // Upsell source "related" / "complementary" (Theme settings): swap in Shopify's recommendations for the
  // product added last (sections/tl-cart-upsells.liquid). The block starts hidden; with no recommendations
  // the Liquid fallback (upsell list, then collection) shows. Cached per product + cart, so a re-render
  // after a quantity change repaints at once instead of hiding and fetching again.
  const recs = {};
  function ups() {
    const box = root && q('[data-tl-ups-url]', root);
    if (!box) return;
    const url = box.dataset.tlUpsUrl;
    const k = url + '|' + (cart ? cart.items.map(i => i.product_id).join() : '');
    const paint = html => {
      if (!box.isConnected) return;
      const row = q('[data-tl-ups-row]', box);
      if (html) row.innerHTML = html;
      box.hidden = !q('li', row);
    };
    if (k in recs) return paint(recs[k]);
    fetch(url).then(r => (r.ok ? r.text() : '')).then(t => {
      const r = t && q('[data-tl-ups-row]', parse(t));
      recs[k] = r && q('li', r) ? r.innerHTML : '';
      paint(recs[k]);
    }).catch(() => paint(''));
  }

  // Empty the error slot, so an old message never greets a reopened drawer or a new request.
  const clearErr = () => { const el = q('[data-tl-error]', root); if (el) { el.hidden = true; el.textContent = ''; } };

  const post = (url, body) => {
    clearErr();
    seq++;
    root.classList.add('is-busy');
    return fetch(url, { method: 'POST', body, headers: { Accept: 'application/json' } })
      .then(r => r.json().then(j => { if (!r.ok) throw j; return j; }))
      .finally(() => root.classList.remove('is-busy'));
  };

  // Re-sync with the server cart (another tab or the /cart page may have changed it). Skipped while an
  // add/change is in flight: its own response paints the drawer.
  const refresh = () => {
    const s = seq;
    return root && !root.classList.contains('is-busy')
      ? fetch((R.home || '/') + '?sections=' + ids()).then(r => r.json()).then(j => { if (s == seq) render(j, 0, 1); }).catch(() => {})
      : Promise.resolve();
  };

  // Resolves with the response once the drawer shows the new cart (falls back to a GET refresh).
  const done = (j, announce) => (j.sections && j.sections['tl-cart-drawer']
    ? Promise.resolve(render(j.sections, announce)).then(() => j)
    : refresh().then(() => j));

  // Never fail silently. Shopify's own message for a 422 (sold out, stock limit); anything else (a stale
  // line key's 400 "no valid id or line parameter", a network error) gets the friendly string. The drawer
  // is re-synced first, so its lines and Total match Shopify again, then the message goes in the error
  // slot (--tl-sale). Impulse's ajaxProduct:error goes out on the form while it is still in the page.
  function fail(e, form, btn) {
    const m = e && e.status == 422 && typeof e.description == 'string' ? e.description : root.dataset.tlErr;
    if (form) fire(form, 'ajaxProduct:error', { errorMessage: m });
    return refresh().then(() => {
      const el = q('[data-tl-error]', root);
      qa('.tl-cart__qty input', root).forEach(i => { i.value = i.defaultValue; }); // back to the real quantities
      open(btn, 1);
      if (el) {
        el.textContent = m;
        el.hidden = false;
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  // fresh: the caller has just painted the drawer from Shopify's response; otherwise re-sync in the background.
  function open(o, fresh) {
    if (!root) { location.href = R.cartPage || '/cart'; return; }
    if (!fresh) refresh();
    if (isOpen()) return;
    opener = o || d.activeElement;
    const b = q('.tl-cart__body', root);
    if (b) b.scrollTop = 0; // start at the reward bar and the newest line
    H.classList.add('tl-cart-open');
    inert(1);
    links();
    fire(d, 'drawerOpen'); // Impulse closes its modals (quick shop) on this
    fire(d, 'tl:cart:open');
    closeBtn().focus();
  }

  function close() {
    if (!isOpen()) return;
    H.classList.remove('tl-cart-open');
    inert(0);
    clearErr();
    links();
    fire(d, 'tl:cart:close');
    if (opener && opener.isConnected && opener.focus) opener.focus();
    opener = null;
  }

  // Stepper taps made while a request is in flight are queued (the field shows the wanted quantity at once)
  // and sent as one change after the drawer has repainted, so quick taps are never lost.
  let pend = null;
  const change = (id, quantity) => post(R.cartChange || '/cart/change.js', new URLSearchParams({
    id, quantity, sections: ids(), sections_url: location.pathname
  })).then(j => done(j, 1), e => fail(e)).then(() => {
    const p = pend;
    pend = null;
    if (p) change(p.k, p.q);
  });

  // Every add-to-cart form on the site (PDP, sticky bar via form=, quick shop, drawer upsells).
  // Skipped on the cart page, where Impulse's own flow reloads the page.
  window.addEventListener('submit', e => {
    const f = e.target;
    if (!root || !f.matches || !f.matches('form[action*="/cart/add"]') || d.body.classList.contains('template-cart')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (f.tlBusy) return;
    f.tlBusy = 1;
    const sub = e.submitter;
    const btns = [...f.elements].filter(b => b.type == 'submit');
    const btn = sub || btns[0];
    const up = root.contains(f); // a drawer upsell
    let fd;
    try { fd = new FormData(f, sub); } catch (x) { fd = new FormData(f); }
    fd.append('sections', ids());
    fd.append('sections_url', location.pathname);
    btns.forEach(b => b.classList.add('btn--loading'));
    post(R.cartAdd || '/cart/add.js', fd)
      .then(j => {
        // Impulse's compat event before the re-render: a drawer upsell's form is replaced by it, and a
        // detached form's event would never reach listeners on document.
        fire(f, 'ajaxProduct:added', { product: j, addToCartBtn: btn });
        return done(j, 1);
      })
      .then(() => {
        if (up) {
          // The card is gone: show the reward bar and the new line (newest is first) and put focus on it.
          q('.tl-cart__body', root).scrollTop = 0;
          const n = q('.tl-cart__name', root);
          if (n) n.focus({ preventScroll: true });
        }
        open(btn, 1);
        btns.forEach(b => {
          b.classList.add('tl-atc-added');
          setTimeout(() => b.classList.remove('tl-atc-added'), 1400);
        });
      }, x => fail(x, f, btn))
      .finally(() => {
        f.tlBusy = 0;
        btns.forEach(b => b.classList.remove('btn--loading'));
      });
  }, true);

  if (root) {
    root.addEventListener('click', e => {
      const t = e.target.closest('[data-tl-close],[data-tl-q]');
      if (!t) return;
      if (t.dataset.tlQ == null) return close();
      const k = t.dataset.tlKey;
      const i = q('input[data-tl-key="' + k + '"]', root);
      // The field shows the quantity being asked for right away (fail() puts the real one back).
      if (!root.classList.contains('is-busy')) {
        if (i) i.value = t.dataset.tlQ;
        return change(k, t.dataset.tlQ);
      }
      if (!i) return;
      const base = pend && pend.k == k ? pend.q : +i.value;
      pend = { k, q: t.classList.contains('tl-cart__rm') ? 0 : Math.max(0, base + (+t.dataset.tlQ - +i.defaultValue)) };
      i.value = pend.q;
    });
    // Typed quantity. An empty or invalid field snaps back to the current quantity; only an explicit 0
    // (or the trash button) removes the line, so clearing the field to retype never loses the item.
    root.addEventListener('change', e => {
      const t = e.target;
      if (t.tagName != 'INPUT' || !t.dataset.tlKey) return;
      const v = parseInt(t.value, 10);
      if (isNaN(v) || v < 0) t.value = t.defaultValue;
      else if (v != t.defaultValue) change(t.dataset.tlKey, v);
    });
    // Header bag icon opens the drawer (href="/cart" stays for JS-off).
    d.addEventListener('click', e => {
      const a = e.target.closest && e.target.closest('.js-drawer-open-cart');
      if (a) { e.preventDefault(); open(a); }
    });
    d.addEventListener('keydown', e => {
      if (!isOpen()) return;
      if (e.key == 'Escape') { close(); return; }
      if (e.key != 'Tab') return;
      const f = qa('a[href],button,input,select,textarea,[tabindex]', panel)
        .filter(x => x.tabIndex >= 0 && !x.disabled && x.getClientRects().length);
      const first = f[0];
      const last = f[f.length - 1];
      const c = d.activeElement;
      // Focus on the panel itself (after a click on plain text inside it) wraps too, else Shift+Tab would
      // leave the dialog.
      if (!panel.contains(c) || c == panel || c == (e.shiftKey ? first : last)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    });
    d.addEventListener('modalOpen', close);
    d.addEventListener('shopify:section:load', links);
    // Back to this tab, or back from the bfcache: the cart may have changed elsewhere.
    d.addEventListener('visibilitychange', () => { if (!d.hidden) refresh(); });
    window.addEventListener('pageshow', e => { if (e.persisted) refresh(); });
  }
  links();
  ups();

  // Server-rendered cart, once, after DOMContentLoaded (a task later, so listeners that other deferred
  // scripts add in their own DOMContentLoaded handlers get it too). 'load' covers a late (non-deferred) load.
  let booted;
  const boot = () => setTimeout(() => { if (!booted && cart) { booted = 1; fire(d, 'tl:cart:updated', cart); } });
  if (d.readyState == 'complete') boot();
  else { d.addEventListener('DOMContentLoaded', boot); window.addEventListener('load', boot); }

  window.TLCart = { open, close, refresh, get cart() { return cart; } };
})();
